/**
 * Compatibility shim.
 *
 * The module this replaces posted the caller's own `accountnumber` and
 * `unique_ID` to keyon to ask what that user was allowed to do. Two things were
 * wrong with it: the endpoint never checked that the requester *was* the subject
 * — any caller could enumerate any user's effective privileges — and the
 * identity itself was guessed from whichever of `externid2` / `externId2` /
 * `externid` / `externId` happened to be present on the cached profile blob.
 *
 * Identity now comes from the access token, and permissions come from
 * `GET /api/v1/me`. Nothing needs to be resolved or sent. The two exports stay
 * so the eight files still importing them keep compiling; each disappears as its
 * component migrates to `useSessionStore()`.
 *
 * @deprecated Read `useSessionStore().permissions` / `.can(scope)` instead.
 */

import { useSessionStore } from '@/stores/session';

/**
 * @deprecated Identity is in the token. Returns the signed-in user for the
 * benefit of call sites that only wanted to know who they were.
 *
 * Every caller still passes main's `(allData, sessionSelf)`. Neither is read:
 * main resolved the actor from them, falling back to `allData.patient`, which
 * on the port is the open chart — or, with no chart open, a stub with no
 * person in it. Taking that branch left a staff member with no chart open
 * `unique_ID: ''`, so the presence check below emptied the nurse, aide and
 * allied rosters without sending a request (review of 8233d594). The session
 * is the token's subject whatever chart is open (decision D4); before it has
 * loaded both ids are null and the callers wait.
 */
export function resolveKeyonPrivilegeIdentity() {
  const session = useSessionStore();

  return {
    userId: session.userId,
    organizationId: session.organizationId,

    // The legacy field names, carrying the real identity.
    //
    // These were returned as `null` on the reasoning that there is nothing to
    // put in a request body any more — which is true of the *body*, and wrong
    // about what the callers do with them. Twenty-odd call sites destructure
    // these two names, and most use them as a presence check before fetching:
    //
    //     const { accountnumber, unique_ID } = resolveKeyonPrivilegeIdentity(allData);
    //     const ctx = { externId: unique_ID, externId2: accountnumber };
    //     if (!ctx.externId || !ctx.externId2) return;   // <- always taken
    //
    // With both null that guard always fired, so the provider, allied, aide,
    // nurse, team and patient card grids returned empty *without issuing a
    // request at all* — no error, no network entry, just "No Providers found"
    // on a chart that has providers.
    //
    // Supplying the real values costs nothing on the wire: the bridge builds
    // its own REST path and the API takes identity from the token, so these
    // never reach the server as authority. They exist so a presence check means
    // "is someone signed in", which is what the call sites are actually asking.
    accountnumber: session.organizationNumber,
    unique_ID: session.userId,
  };
}

/** @deprecated Permissions arrive with the session; there is no separate fetch. */
export async function fetchUserPrivilegeAssignments() {
  const session = useSessionStore();

  if (!session.me) {
    await session.hydrate();
  }

  return {
    ok: true,
    data: session.permissions.map((code) => ({
      id: code,
      code,
      effective_granted: true,
      user_override: null,
    })),
    error: null,
  };
}

export default { resolveKeyonPrivilegeIdentity, fetchUserPrivilegeAssignments };
